import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import {
  doChangeToDraft,
  doChangeToUnassigned,
  doCreateOrder,
  doDeleteOrders,
  doGetOrder,
  doGetOrders,
  doUpdateOrder,
  doUploadOrders,
} from './thunk';

const isActionWithPending = (action) => {
  return action.type && action.type.startsWith('Orders/') && action.type.endsWith('/pending');
};

const isActionWithFulfilled = (action) => {
  return action.type && action.type.startsWith('Orders/') && action.type.endsWith('/fulfilled');
};

const isActionWithFailed = (action) => {
  return action.type && action.type.startsWith('Orders/') && action.type.endsWith('/rejected');
};

const ordersAdapter = createEntityAdapter({
  selectId: (item) => item.orderCode,
});

const orderSlice = createSlice({
  name: 'order',
  initialState: ordersAdapter.getInitialState({
    action: null,
    isFetching: false,
    currentOrder: null,
    status: 'idle',
    metadata: {},
    item: null,
  }),
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(doGetOrders.pending, (state) => {
      state.action = 'getall';
      state.status = 'loading';
    });
    builder.addCase(doGetOrders.fulfilled, (state, action) => {
      const { orders, pagination } = action.payload;
      state.status = 'succeeded';
      state.metadata = pagination;
      ordersAdapter.setAll(state, orders || []);
    });
    builder.addCase(doGetOrders.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doGetOrder.pending, (state) => {
      state.action = 'getOne';
      state.status = 'loading';
    });
    builder.addCase(doGetOrder.fulfilled, (state, action) => {
      state.status = 'succeeded';
      // OVO-028 — always reflect the payload, including a null/empty
      // result for a deleted order. The old `if (action.payload)` guard
      // kept the previously-viewed order in state, so a deleted order
      // rendered a stale detail shell instead of a "not found" page.
      state.currentOrder = action.payload || null;
    });
    builder.addCase(doGetOrder.rejected, (state) => {
      state.status = 'failed';
      // The backend now 404s on a missing order — drop any stale order so
      // the detail page can show its "not found" state.
      state.currentOrder = null;
    });
    builder.addCase(doCreateOrder.pending, (state) => {
      state.action = 'create';
      state.status = 'loading';
    });
    builder.addCase(doCreateOrder.fulfilled, (state) => {
      state.status = 'succeeded';
    });
    builder.addCase(doChangeToUnassigned.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doChangeToUnassigned.pending, (state) => {
      state.action = 'changeToUnassigned';
      state.status = 'loading';
    });
    builder.addCase(doChangeToUnassigned.fulfilled, (state) => {
      state.status = 'succeeded';
    });
    builder.addCase(doChangeToDraft.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doChangeToDraft.pending, (state) => {
      state.action = 'changeToDraft';
      state.status = 'loading';
    });
    builder.addCase(doChangeToDraft.fulfilled, (state) => {
      state.status = 'succeeded';
    });
    builder.addCase(doCreateOrder.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doUpdateOrder.pending, (state) => {
      state.action = 'update';
      state.status = 'loading';
    });
    builder.addCase(doUpdateOrder.fulfilled, (state) => {
      state.status = 'succeeded';
    });
    builder.addCase(doUpdateOrder.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doUploadOrders.pending, (state) => {
      state.action = 'upload';
      state.status = 'loading';
    });
    builder.addCase(doUploadOrders.fulfilled, (state) => {
      state.status = 'succeeded';
    });
    builder.addCase(doUploadOrders.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doDeleteOrders.pending, (state) => {
      state.status = 'loading';
      state.action = 'deleteOrders';
    });
    builder.addCase(doDeleteOrders.fulfilled, (state) => {
      state.status = 'succeeded';
    });
    builder.addCase(doDeleteOrders.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addMatcher(isActionWithPending, (state) => {
      state.isFetching = true;
      state.message = null;
    });
    builder.addMatcher(isActionWithFulfilled, (state) => {
      state.isFetching = false;
    });
    builder.addMatcher(isActionWithFailed, (state) => {
      state.isFetching = false;
      state.message = null;
    });
  },
});

export const {
  selectAll: selectAllOrders,
  selectById: selectOrderById,
  selectIds: selectOrdersIds,
} = ordersAdapter.getSelectors((state) => state.order);
const { reducer: OrderReducer } = orderSlice;
export default OrderReducer;
